import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import "./Auth.css";

const FAQS = [
  {
    q: "What is Match Mixer?",
    a: "Match Mixer is a football prediction game where you compete in private leagues with friends. Predict match scores, earn points, and climb the leaderboard.",
  },
  {
    q: "How do leagues work?",
    a: "Create a private league and share the join code with friends. Everyone predicts the same fixtures each matchday, then points are awarded for correct results (home win/draw/loss) with bonus points for exact scorelines.",
  },
  {
    q: "What is Last One Standing?",
    a: "Last One Standing is a knockout game mode within a league. Each matchday you pick one team to win — if they don't, you're out. Last player standing wins.",
  },
  {
    q: "When can I submit predictions?",
    a: "You can submit or update predictions 30 minutes before a fixture kicks off. Once that deadline passes, your predictions are locked.",
  },
];

const SLIDE_COUNT = 3;

export default function Auth() {
  const { signUp, signIn, signInWithGoogle, error } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [faqOpen, setFaqOpen] = useState(false);
  const [signinOpen, setSigninOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(
      () => setCurrentSlide((s) => (s + 1) % SLIDE_COUNT),
      6000
    );
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  function goToSlide(n) {
    setCurrentSlide(((n % SLIDE_COUNT) + SLIDE_COUNT) % SLIDE_COUNT);
    startTimer();
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError("");
    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setLocalError("Passwords do not match");
          setLoading(false);
          return;
        }
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setLocalError(error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setLocalError("");
    try {
      await signInWithGoogle();
    } catch (err) {
      setLocalError(error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-body">
        <div className="auth-shell">
          <div className="auth-top-row">

            {/* LEFT: logo + tagline */}
            <section className="auth-brand-column" aria-label="Brand">
              <div className="auth-brand-content">
                <img
                  src={`${process.env.PUBLIC_URL}/Logo/logo-with-text.svg`}
                  alt="Match Mixer"
                  className="auth-brand-logo"
                />
                <p className="auth-brand-tagline">
                  Predict &middot; Compete &middot; Win
                </p>
              </div>
            </section>

            {/* RIGHT: collapsible sign-in card + rotating game slides */}
            <section className="auth-right-column" aria-label="Sign in panel">

              {/* Sign-in card — collapses to a single bar on load */}
              <div className="auth-card">
                <button
                  type="button"
                  className={`auth-signin-toggle${signinOpen ? " is-open" : ""}`}
                  onClick={() => setSigninOpen((prev) => !prev)}
                >
                  <span />
                  <span>{isSignUp ? "Create account" : "Sign in"}</span>
                  <span
                    className="auth-chevron"
                    style={{
                      transform: signinOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  >
                    ▼
                  </span>
                </button>

                <div
                  className={`auth-form-collapsible${
                    signinOpen ? " is-open" : ""
                  }`}
                >
                  <div className="auth-form-collapsible-inner">
                    <div className="auth-form-inner-pad">
                      <div className="auth-card-top">
                        <button
                          type="button"
                          disabled={loading}
                          onClick={handleGoogleSignIn}
                          className="google-button"
                        >
                          Continue with Google
                        </button>
                        <div className="auth-divider" aria-hidden="true">
                          <span>or</span>
                        </div>
                      </div>

                      <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-group">
                          <label htmlFor="email">Email</label>
                          <input
                            id="email"
                            type="email"
                            autoComplete="username"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={loading}
                            className="form-input"
                            placeholder="your@email.com"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="password">Password</label>
                          <input
                            id="password"
                            type="password"
                            autoComplete={
                              isSignUp ? "new-password" : "current-password"
                            }
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            className="form-input"
                            placeholder="••••••"
                          />
                        </div>

                        {isSignUp && (
                          <div className="form-group">
                            <label htmlFor="confirmPassword">
                              Confirm Password
                            </label>
                            <input
                              id="confirmPassword"
                              type="password"
                              autoComplete="new-password"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              required
                              disabled={loading}
                              className="form-input"
                              placeholder="••••••"
                            />
                          </div>
                        )}

                        {(localError || error) && (
                          <div className="error-message">
                            {localError || error}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={loading}
                          className="auth-button"
                        >
                          {loading
                            ? "Loading..."
                            : isSignUp
                            ? "Sign Up"
                            : "Sign In"}
                        </button>
                      </form>

                      <div className="auth-footer">
                        <p>
                          {isSignUp
                            ? "Already have an account?"
                            : "Don't have an account?"}
                          <button
                            type="button"
                            onClick={() => {
                              setIsSignUp((prev) => !prev);
                              setLocalError("");
                              setEmail("");
                              setPassword("");
                              setConfirmPassword("");
                            }}
                            className="toggle-button"
                          >
                            {isSignUp ? "Sign In" : "Sign Up"}
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Game preview slides — collapse when sign-in form opens */}
              <div
                className={`auth-slides-wrapper${
                  signinOpen ? " is-collapsed" : ""
                }`}
              >
                <div className="auth-slides-inner">
                  <div className="auth-snippet-slides">

                    {/* Slide 0: Score Predictor */}
                    <div
                      className={`auth-snippet-slide predictions-card${
                        currentSlide === 0 ? " is-active" : ""
                      }`}
                    >
                      <div className="card-heading-row">
                        <h3>Score Predictor</h3>
                        <span className="auth-league-badge">Global League</span>
                      </div>
                      <div className="auth-snippet-fixtures">
                      <div className="fixture-row">
                        <div className="fixture-title">
                          <span className="fixture-team">Arsenal</span>
                          <span className="vs">vs</span>
                          <span className="fixture-team">Chelsea</span>
                          <span className="fixture-status-chip status-finished">FT</span>
                          <span className="fixture-kickoff-time">2&ndash;1</span>
                        </div>
                        <div className="prediction-block">
                          <div className="score-inputs prediction-exact">
                            <input className="score-input" type="number" value="2" readOnly />
                            <input className="score-input" type="number" value="1" readOnly />
                          </div>
                          <span className="prediction-summary">
                            Exact &middot; 3 pts
                          </span>
                        </div>
                      </div>
                      <div className="fixture-row">
                        <div className="fixture-title">
                          <span className="fixture-team">Man City</span>
                          <span className="vs">vs</span>
                          <span className="fixture-team">Liverpool</span>
                          <span className="fixture-status-chip status-finished">FT</span>
                          <span className="fixture-kickoff-time">3&ndash;1</span>
                        </div>
                        <div className="prediction-block">
                          <div className="score-inputs prediction-result">
                            <input className="score-input" type="number" value="2" readOnly />
                            <input className="score-input" type="number" value="0" readOnly />
                          </div>
                          <span className="prediction-summary">
                            Correct &middot; 1 pt
                          </span>
                        </div>
                      </div>
                      <div className="fixture-row fixture-row-last" style={{marginBottom: 0}}>
                        <div className="fixture-title">
                          <span className="fixture-team">Spurs</span>
                          <span className="vs">vs</span>
                          <span className="fixture-team">Man Utd</span>
                          <span className="fixture-status-chip status-finished">FT</span>
                          <span className="fixture-kickoff-time">0&ndash;0</span>
                        </div>
                        <div className="prediction-block">
                          <div className="score-inputs prediction-incorrect">
                            <input className="score-input" type="number" value="2" readOnly />
                            <input className="score-input" type="number" value="1" readOnly />
                          </div>
                          <span className="prediction-summary">
                            Incorrect &middot; 0 pts
                          </span>
                        </div>
                      </div>
                      </div>
                    </div>

                    {/* Slide 1: Leaderboard */}
                    <div
                      className={`auth-snippet-slide predictions-card${
                        currentSlide === 1 ? " is-active" : ""
                      }`}
                    >
                      <div className="card-heading-row">
                        <h3>Leaderboard</h3>
                        <span className="auth-league-badge">Global League</span>
                      </div>
                      <div className="leaderboard-table-wrap">
                        <table className="leaderboard-table">
                          <thead>
                            <tr>
                              <th>Rank</th>
                              <th>Player</th>
                              <th>Pts</th>
                              <th>Exact</th>
                              <th>Correct Results</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="leaderboard-row-winner">
                              <td>
                                <span className="leaderboard-winner-cup">
                                  🏆
                                </span>
                                1
                              </td>
                              <td>Jordan</td>
                              <td>24</td>
                              <td>7</td>
                              <td>3</td>
                            </tr>
                            <tr>
                              <td>2</td>
                              <td>Priya</td>
                              <td>21</td>
                              <td>6</td>
                              <td>3</td>
                            </tr>
                            <tr className="leaderboard-row-current">
                              <td>3</td>
                              <td>Liam</td>
                              <td>18</td>
                              <td>5</td>
                              <td>3</td>
                            </tr>
                            <tr>
                              <td>4</td>
                              <td>Sam</td>
                              <td>15</td>
                              <td>4</td>
                              <td>3</td>
                            </tr>
                            <tr>
                              <td>5</td>
                              <td>Chloe</td>
                              <td>12</td>
                              <td>3</td>
                              <td>3</td>
                            </tr>
                            <tr>
                              <td>6</td>
                              <td>Kai</td>
                              <td>9</td>
                              <td>2</td>
                              <td>3</td>
                            </tr>
                            <tr>
                              <td>7</td>
                              <td>Maya</td>
                              <td>6</td>
                              <td>1</td>
                              <td>3</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="helper-text auth-snippet-footnote">
                        Matchday 1 &middot; 7 of 20 players
                      </p>
                    </div>

                    {/* Slide 2: Last One Standing */}
                    <div
                      className={`auth-snippet-slide predictions-card${
                        currentSlide === 2 ? " is-active" : ""
                      }`}
                    >
                      <div className="card-heading-row">
                        <h3>Last One Standing</h3>
                      </div>
<div className="auth-los-divider">Gameweek 3 picks</div>
                      <div className="auth-los-player-row">
                        <span className="auth-los-player-name">Liam</span>
                        <span className="auth-los-player-pick">Arsenal</span>
                      </div>
                      <div className="auth-los-player-row">
                        <span className="auth-los-player-name">Jordan</span>
                        <span className="auth-los-player-pick">Man City</span>
                      </div>
                      <div className="auth-los-player-row">
                        <span className="auth-los-player-name">Priya</span>
                        <span className="auth-los-player-pick">Liverpool</span>
                      </div>
                      <div className="auth-los-player-row">
                        <span className="auth-los-player-name">Sam</span>
                        <span className="auth-los-player-pick">Chelsea</span>
                      </div>
                      <div className="auth-los-player-row">
                        <span className="auth-los-player-name">Chloe</span>
                        <span className="auth-los-player-pick">Wolves</span>
                      </div>
                      <div className="auth-los-divider auth-los-divider--spaced">
                        Eliminated
                      </div>
                      <div className="auth-los-player-row">
                        <span className="auth-los-player-name auth-los-player-name--out">
                          Alex
                        </span>
                        <span className="auth-los-player-pick auth-los-player-pick--out">
                          GW2 &middot; Spurs lost
                        </span>
                      </div>
                      <div className="auth-los-player-row auth-los-player-row--last">
                        <span className="auth-los-player-name auth-los-player-name--out">
                          Kai
                        </span>
                        <span className="auth-los-player-pick auth-los-player-pick--out">
                          GW1 &middot; Everton lost
                        </span>
                      </div>
                    </div>

                  </div>

                  <div className="auth-snippet-dots">
                    {[0, 1, 2].map((i) => (
                      <button
                        key={i}
                        className={`auth-snippet-dot${
                          currentSlide === i ? " is-active" : ""
                        }`}
                        onClick={() => goToSlide(i)}
                        aria-label={`Slide ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

            </section>
          </div>

          <p className="auth-tagline">
            Predict match scores in private leagues with friends, or survive in
            Last One Standing.
          </p>

          <section className={`auth-faq-card${faqOpen ? " is-open" : ""}`}>
            <button
              type="button"
              className="auth-faq-toggle"
              onClick={() => setFaqOpen((prev) => !prev)}
              aria-expanded={faqOpen}
            >
              <span>How to play</span>
              <span className="auth-faq-toggle-icon" aria-hidden="true">
                {faqOpen ? "▲" : "▼"}
              </span>
            </button>
            {faqOpen && (
              <div className="auth-faq-content">
                {FAQS.map((item) => (
                  <article key={item.q} className="auth-faq-entry">
                    <h3>{item.q}</h3>
                    <p>{item.a}</p>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
